import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  Heading,
  Link,
  Spinner,
  Tag,
  Text,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetBseBidQuery, useGetProductGroupsAsSupplierQuery } from "../../../autogen/bff-api";
import { LastChangedTag } from "../../../common/editing/LastChangedTag/LastChangedTag";
import { useQueryParams } from "../../../common/useQueryParams";
import { urls } from "../../../urls";
import { useBasicBidState } from "../../sourcing-events/useBasicBidEventState";
import { Details } from "./Details";
import { Documents } from "./Documents";
import styles from "./EditBasicBidForm.module.css";
import { Navigation } from "./Navigation";
import { Review } from "./Review";
import { Products } from "./products/Products";

export const EditBasicBidForm = () => {
  const params = useQueryParams();
  const currentStep = params.get("step");
  const navigate = useNavigate();
  const bidState = useBasicBidState();
  const { data, isLoading } = useGetProductGroupsAsSupplierQuery({ sourcingEventId: bidState.forEvent.id });

  const [showDrawer, setShowDrawer] = useState(false);
  const [isProductsVisible, setIsProductsVisible] = useState<boolean>(false);

  const currentRound = bidState.forEvent.negotiationRounds?.find((r) => r.id === bidState.forNegotiationRound);
  const previousRound =
    currentRound && currentRound.roundNumber && currentRound.roundNumber > 1
      ? bidState?.forEvent.negotiationRounds?.find((r) => r.roundNumber === currentRound?.roundNumber - 1)
      : undefined;
  const previousBidId =
    previousRound?.bidId ?? (currentRound?.roundNumber === 1 ? bidState.forEvent.createdBidId : undefined);

  const { data: previousBid } = useGetBseBidQuery(previousBidId ? { bidId: previousBidId } : skipToken);

  useEffect(() => {
    setIsProductsVisible(!!data?.productGroups?.length);
  }, [data?.productGroups?.length]);

  const navigateToDetails = useCallback(
    () => navigate(urls.bids.edit.go(bidState.id).details),
    [bidState.id, navigate]
  );
  const navigateToProducts = isProductsVisible ? () => navigate(urls.bids.edit.go(bidState.id).products) : undefined;
  const navigateToDocuments = () => navigate(urls.bids.edit.go(bidState.id).documents);
  const navigateToReview = () => navigate(urls.bids.edit.go(bidState.id).review);
  const navigateToEventView = () =>
    navigate(
      urls.events.supplierView.go({ eventId: bidState.forEvent.id, negotiationRoundId: bidState.forNegotiationRound })
    );

  useEffect(() => {
    if (!currentStep) navigateToDetails();
  }, [currentStep, navigateToDetails]);

  const getTitle = (step: string): string => {
    if (step === "details") return t("Bid details");
    if (step === "review") return t("Review & Send");
    if (step === "products") return t("Products");
    if (step === "documents") return t("Documents");
    throw Error("Unknown step");
  };

  if (isLoading)
    return (
      <Flex width="full" justifyContent={"center"} alignItems={"center"} pt="10">
        <Spinner color="teal" />
      </Flex>
    );

  return (
    <>
      <Drawer placement={"bottom"} onClose={() => setShowDrawer(false)} isOpen={showDrawer}>
        <DrawerOverlay />
        <DrawerContent borderTop="1px solid" borderColor="smBorder">
          <DrawerBody>
            <Navigation
              navigateToDetails={navigateToDetails}
              navigateToProducts={navigateToProducts}
              navigateToDocuments={navigateToDocuments}
              navigateToReview={navigateToReview}
              navigateToEventView={navigateToEventView}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Flex
        display={{ md: "none", base: "flex" }}
        position={"fixed"}
        zIndex={1}
        bottom="0px"
        width="full"
        height="60px"
        borderTop={"1px solid"}
        borderColor="smBorder"
        alignItems="center"
        bg="smBackground"
      >
        <Button variant="solid" colorScheme="blue" width="full" mx="2.5" onClick={() => setShowDrawer(true)}>
          {t("Change step")}
        </Button>
      </Flex>
      <Flex justifyContent={"center"} px="2">
        <Flex width="full" maxWidth="container.xl" justifyContent={"center"}>
          <Grid className={styles.grid} height="full" marginTop={"9px"} width={"full"} maxWidth="full">
            <GridItem area="menuNav" alignItems="end" borderBottom={"1px solid"} borderColor="smBorder">
              <Box display="flex" width="full"></Box>
            </GridItem>
            <GridItem area="contentNav" alignItems="end" borderBottom={"1px solid"} borderColor="smBorder">
              <Box display="flex" width="full" flexDirection={"column"}>
                <Box display={"flex"} pb="2">
                  {currentStep && (
                    <Heading as="h2" size="md" mr="10px">
                      {getTitle(currentStep)}
                    </Heading>
                  )}
                </Box>
                <Box>
                  <Tag colorScheme={"orange"} mr="10px" size={"md"} mb="5px">
                    {t("Draft")}
                  </Tag>
                  <LastChangedTag timestamp={bidState.lastChanged} />
                </Box>
                <Flex flexDirection={"row"}>
                  <Text fontSize={"md"} color="smMuted" mt="3px">
                    {t("Bid for")}{" "}
                    <Link
                      variant={"ghost"}
                      fontWeight={"bold"}
                      color="teal"
                      href={urls.events.supplierView.go({
                        eventId: bidState.forEvent.id,
                        negotiationRoundId: bidState.forNegotiationRound,
                      })}
                    >
                      {bidState.forEvent.title}
                    </Link>{" "}
                    {t("created by")} <b>{bidState.forEvent.owningOrganization.name}</b>
                  </Text>
                </Flex>
              </Box>
            </GridItem>
            <GridItem height="full" area="menu" display={{ md: "block", base: "none" }}>
              <Navigation
                navigateToDetails={navigateToDetails}
                navigateToProducts={navigateToProducts}
                navigateToDocuments={navigateToDocuments}
                navigateToReview={navigateToReview}
                navigateToEventView={navigateToEventView}
              />
            </GridItem>
            <GridItem height="full" maxWidth="full" area="content" overflow={"hidden"}>
              <Box height="full" width={"full"}>
                <Box display={currentStep === "details" ? undefined : "none"} width="full">
                  <Details nextStep={navigateToProducts ?? navigateToDocuments} />
                </Box>
                <Box display={currentStep === "products" ? undefined : "none"} maxWidth="full" overflow={"hidden"}>
                  <Products
                    nextStep={navigateToDocuments}
                    previousStep={navigateToDetails}
                    previousBid={previousBid}
                    displayPreviousProductPrices={!!previousBidId}
                  />
                </Box>
                <Box display={currentStep === "documents" ? undefined : "none"}>
                  <Documents previousStep={navigateToProducts ?? navigateToDetails} nextStep={navigateToReview} />
                </Box>
                <Box display={currentStep === "review" ? undefined : "none"}>
                  <Review previousStep={navigateToDocuments} />
                </Box>
              </Box>
            </GridItem>
          </Grid>
        </Flex>
      </Flex>
    </>
  );
};
